import { beautyKit2Loader, getImageCapture } from './BeautyKit2Loader.js';
import { Effect, Layer, Shapetype } from './Types';

declare global {
	interface Window {
		dataLayer: Record<string, any>[];
	}
}

class BeautyKit2 {
	private _bk2Module: any = null;
	private _bk2CPP: any = null;
	private _drawingLoop: ReturnType<typeof setInterval> | null = null;
	private _isWASMReady = 0;
	private _videoTrack: MediaStreamTrack | null = null;

	resolution = this.resByDeviceCategory();
	score = 0;
	framerate = this.resolution === 720 ? 1000 / 24 : 1000 / 8;

	resByDeviceCategory() {
		const screenRes = window.devicePixelRatio * window.devicePixelRatio * window.screen.availHeight * window.screen.availWidth;
		const ratio = window.screen.availHeight / window.screen.availWidth;

		if (['iPad', 'iPad Simulator', 'MacIntel'].includes(navigator.platform)) {
			return window.innerHeight > 2300 || window.innerWidth > 1000 ? 720 : 480;
		}

		if ((ratio > 2 && ['iPhone', 'iPhone Simulator'].includes(navigator.platform)) || (screenRes > 2500000 && window.innerHeight > 700) || window.innerWidth > 768) {
			return 720;
		}

		return screenRes > 1400000 ? 480 : 360;
	}

	private loadScript(src: string) {
		return new Promise<void>((resolve, reject) => {
			const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

			if (existingScript?.dataset.loaded === 'true') {
				resolve();
				return;
			}

			const script = existingScript ?? document.createElement('script');
			script.src = src;
			script.async = true;

			script.addEventListener('load', () => {
				script.dataset.loaded = 'true';
				resolve();
			}, { once: true });
			script.addEventListener('error', () => reject(new Error(`[BK2] Failed to load ${src}`)), { once: true });

			if (!existingScript) {
				document.body.appendChild(script);
			}
		});
	}

	load() {
		return new Promise<void>((resolve, reject) => {
			if (this._isWASMReady !== 0) {
				if (this._bk2Module) {
					resolve();
				}
				return;
			}

			const publicUrl = process.env.PUBLIC_URL || '';
			Promise.all([
				this.loadScript(`${publicUrl}/image-capture.js`),
				this.loadScript(`${publicUrl}/BeautyKit2.js`),
			])
				.then(() => {
					beautyKit2Loader()
						.then((module) => {
							this._bk2Module = module;
							const ppixels = this._bk2Module._malloc(this.resolution * this.resolution * 4);
							this._bk2Module.pixels = new Uint8ClampedArray(this._bk2Module.HEAPU8.buffer, ppixels, this.resolution * this.resolution * 4);
							this._isWASMReady = 1;
							resolve();
						})
						.catch(reject);
				})
				.catch(reject);
		});
	}

	initCamera(facingMode: boolean) {
		return new Promise((resolve, reject) => {
			navigator.mediaDevices
				.getUserMedia({
					video: {
						width: { ideal: this.resolution },
						height: { ideal: this.resolution },
						facingMode: facingMode ? 'user' : 'environment',
					},
					audio: false,
				})
				.then((mediaStream) => {
					if (this._videoTrack === null) {
						this._videoTrack = mediaStream.getVideoTracks()[0];
					}

					resolve(getImageCapture(this._videoTrack));
				})
				.catch(reject);
		});
	}

	start(videoCanvas: string, renderingCanvas: string, facingMode: boolean) {
		if (this._bk2CPP) {
			this.stop();
		}

		this._bk2CPP = new this._bk2Module.BeautyKit();
		this._bk2CPP.setCanvasSize(this.resolution, this.resolution);
		this._bk2CPP.setFrameSize(this.resolution, this.resolution);

		return new Promise<void>((resolve, reject) => {
			const cameraCanvas = document.getElementById(videoCanvas) as HTMLCanvasElement | null;
			const ctx = cameraCanvas?.getContext('2d');

			if (!cameraCanvas || !ctx) {
				reject(new Error('[BK2] Missing camera canvas'));
				return;
			}

			this.initCamera(facingMode)
				.then((imageCapture: any) => {
					this._bk2CPP.initialize(renderingCanvas);
					this._drawingLoop = setInterval(() => {
						if (imageCapture?.track && (imageCapture.track.readyState !== 'live' || !imageCapture.track.enabled || imageCapture.track.muted)) {
							return;
						}

						imageCapture
							.grabFrame(this.resolution)
							.then((bmp: CanvasImageSource) => {
								try {
									ctx.drawImage(bmp, 0, 0);
									this._bk2Module.pixels = ctx.getImageData(0, 0, this.resolution, this.resolution).data;
								} catch (error) {
									this.handleError(error);
								}

								this._bk2CPP.setFrame();
								this.score = this._bk2CPP.detect();
								this._bk2CPP.draw(1.0);
							})
							.catch((error: unknown) => this.handleError(error));
					}, this.framerate);
					resolve();
				})
				.catch(reject);
		});
	}

	stop() {
		return new Promise<void>((resolve) => {
			if (this._videoTrack) {
				this._videoTrack.enabled = false;
				this._videoTrack.stop();
				this._videoTrack = null;
			}

			if (this._drawingLoop) {
				clearInterval(this._drawingLoop);
				this._drawingLoop = null;
			}

			this._bk2CPP = null;
			resolve();
		});
	}

	setLayers(layers: Layer[]) {
		if (!this._bk2CPP) {
			return;
		}

		const vectors = new this._bk2Module.VectorARLayer();

		layers.forEach((layer) => {
			const shape = layer.shape.startsWith('/') ? layer.shape.slice(1) : layer.shape;
			vectors.emplace_back(0, layer.shapeType, layer.r, layer.g, layer.b, layer.a, layer.effect, `/shapes/${shape}`, false);
		});

		this._bk2CPP.setLayers(vectors.getObject());
	}

	private handleError(error: unknown) {
		if (typeof error === 'number' && this._bk2Module) {
			console.error(this._bk2Module.getExceptionMessage(error));
		} else {
			console.error(error);
		}
	}
}

export { BeautyKit2, Effect, Layer, Shapetype };
